import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import { useDropdown } from '@/hooks/useDropdown';
import { useDynamicLocale } from '@/hoslog-intl';
import ChevronDown from '@/icons/chevron-down';
import { useEffect } from 'react';
import { z } from 'zod';
import { useGetClubPlanDetail } from '../hooks/useGetClubPlanDetail';
import { useManageOriginalPlanMutation, useUpdateClubPlanMutation } from '../hooks/useManageOriginalPlansMutation';

const updatePlanSchema = z.object({
  planPrice: z.string().min(1, { message: 'common.form.validation.required' }),
  lastEntryTime: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  noOfPeople: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  nomination: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
  planTime: z.string().refine((value) => value !== '' && value !== 'default', {
    message: 'common.form.validation.required',
  }),
});

type DefaultValues = {
  lastEntryTime: string;
  nomination: string;
  noOfPeople: string;
  planPrice: string;
  planTime: string;
};

const noOfPeopleOption = [
  {
    value: '1',
    label: '1名',
  },
  {
    value: '2',
    label: '2名',
  },
  {
    value: '3',
    label: '3名',
  },
  {
    value: '4',
    label: '4名',
  },
  {
    value: '5',
    label: '5名',
  },
  {
    value: '6',
    label: '6名',
  },
  {
    value: '7',
    label: '7名',
  },
  {
    value: '8',
    label: '8名',
  },
  {
    value: '9',
    label: '9名',
  },
  {
    value: '10',
    label: '10名',
  },
];

const ClubPlanFormView = ({
  clubId,
  planId = '',
  commonClubId,
  defaultValues,
  lastEntryTimeOptions,
}: {
  clubId: string;
  planId?: string;
  commonClubId: string | undefined;
  defaultValues: DefaultValues;
  lastEntryTimeOptions: { label: string; value: string }[];
}) => {
  const form = useForm({
    schema: updatePlanSchema,
    mode: 'onBlur',
    defaultValues,
  });

  const { mutateAsync } = useManageOriginalPlanMutation();
  const { mutateAsync: updatePlanMutateAsync } = useUpdateClubPlanMutation();
  const { clubPlanDetails } = useGetClubPlanDetail(clubId, planId);
  const { t } = useDynamicLocale();

  const { data: planTitleOptions = [] } = useDropdown(
    'clubPlanTime',
    commonClubId,
    undefined,
    undefined,
    Boolean(commonClubId),
  );

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  return (
    <Form
      form={form}
      onSubmit={async (formValues) => {
        if (planId) {
          await updatePlanMutateAsync({
            lastEntryTime: formValues.lastEntryTime,
            nomination: Number(formValues.nomination),
            noOfPeople: Number(formValues.noOfPeople),
            planPrice: Number(formValues.planPrice),
            planTime: clubPlanDetails?.planTime || '',
            clubId,
            planId,
          });
          return;
        }
        await mutateAsync({
          lastEntryTime: formValues.lastEntryTime,
          nomination: Number(formValues.nomination),
          noOfPeople: Number(formValues.noOfPeople),
          planPrice: Number(formValues.planPrice),
          planTime: formValues.planTime,
          clubId,
        });
      }}
    >
      <fieldset disabled={!planTitleOptions || (!!planId && !clubPlanDetails)}>
        <div className="grid grid-cols-2 gap-4.5">
          <div className="col-span-2">
            {planId ? (
              <div className="relative">
                <Input label="common.form.label.planTitle" {...form.register('planTime')} disabled />

                <ChevronDown className="absolute right-3 top-10" />
              </div>
            ) : (
              <Select label="common.form.label.planTitle" {...form.register('planTime')} options={planTitleOptions} />
            )}
          </div>
          <div className="relative">
            <Input
              label="common.form.label.planPrice"
              {...form.register('planPrice')}
              inputMode="numeric"
              placeholder={t('storeManagement.premiumStores.planManagement.managePlan.enterPlan')}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 5);

                form.setValue('planPrice', value);
              }}
            />
            <span className="absolute right-3 top-7 text-muted-foreground pointer-events-none">円</span>
          </div>
          <Select
            options={noOfPeopleOption}
            {...form.register('nomination')}
            placeholder={t('storeManagement.premiumStores.planManagement.managePlan.selectNomination')}
            label="common.form.label.nomination"
          />
          <Select
            options={lastEntryTimeOptions}
            {...form.register('lastEntryTime')}
            placeholder="HH:MM"
            label="common.form.label.lastEntryTime"
          />
          <Select
            options={noOfPeopleOption}
            {...form.register('noOfPeople')}
            label="common.form.label.numberOfPeople"
          />
        </div>
      </fieldset>
    </Form>
  );
};

export default ClubPlanFormView;
